import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { BOOKING } from "@/constants/testIds";
import { useLang } from "@/i18n/LangContext";

export default function BookPage() {
  const { t } = useLang();
  const TOPICS = [
    { key: "family", label: t("book.topic.family"), numeral: "I", testId: BOOKING.topicFamily },
    { key: "legal", label: t("book.topic.legal"), numeral: "II", testId: BOOKING.topicLegal },
    { key: "financial", label: t("book.topic.financial"), numeral: "III", testId: BOOKING.topicFinancial },
  ];

  const [topic, setTopic] = useState("family");
  const [mode, setMode] = useState("online");
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setSlot("");
    const d = date.toISOString().slice(0, 10);
    api
      .get(`/availability?date=${d}`)
      .then((r) => setAvailableSlots(r.data.slots || []))
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const canSubmit = topic && mode && date && slot && name && email && phone && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        name, email, phone, topic, mode,
        date: date.toISOString().slice(0, 10),
        time_slot: slot,
        notes,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Could not create checkout");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <Navbar />
      <main className="pt-32 pb-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 md:sticky md:top-32 self-start"
          >
            <div className="mono-eyebrow mb-6">{t("book.eyebrow")}</div>
            <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] font-medium" style={{ letterSpacing: "-0.03em" }}>
              {t("book.title.a")}
              <em style={{ fontStyle: "italic", color: "var(--terra)" }}>{t("book.title.b")}</em>.
            </h1>
            <p className="mt-6 text-base md:text-lg" style={{ color: "var(--ink-2)" }}>{t("book.sub")}</p>
            <div className="mt-10 border-t hairline pt-6">
              <div className="mono-eyebrow">{t("book.payment.title")}</div>
              <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
                {t("book.payment.body")}<br />
                <span className="font-mono text-xs" style={{ color: "var(--mute)" }}>
                  4242 4242 4242 4242 · any future date · any CVC
                </span>
              </p>
            </div>
          </motion.div>

          <motion.form
            data-testid={BOOKING.form}
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-8 space-y-14"
          >
            <section>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="serif italic text-3xl" style={{ color: "var(--terra)" }}>01</span>
                <h2 className="serif text-2xl md:text-3xl" style={{ letterSpacing: "-0.015em" }}>{t("book.step1")}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {TOPICS.map((tp) => (
                  <button
                    key={tp.key}
                    type="button"
                    data-testid={tp.testId}
                    onClick={() => setTopic(tp.key)}
                    className="text-left p-5 border transition-colors"
                    style={{
                      borderColor: topic === tp.key ? "var(--ink)" : "var(--line)",
                      background: topic === tp.key ? "var(--ink)" : "#FFFFFF",
                      color: topic === tp.key ? "var(--cream)" : "var(--ink)",
                    }}
                  >
                    <div className="serif italic text-2xl mb-2" style={{ color: topic === tp.key ? "var(--cream)" : "var(--terra)" }}>
                      {tp.numeral}
                    </div>
                    <div className="serif text-lg">{tp.label}</div>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="serif italic text-3xl" style={{ color: "var(--terra)" }}>02</span>
                <h2 className="serif text-2xl md:text-3xl" style={{ letterSpacing: "-0.015em" }}>{t("book.step2")}</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ModeCard active={mode === "online"} onClick={() => setMode("online")} testId={BOOKING.modeOnline}
                  opt={t("book.opt.a")} title={t("book.mode.online")} sub={t("book.mode.online.sub")} />
                <ModeCard active={mode === "offline"} onClick={() => setMode("offline")} testId={BOOKING.modeOffline}
                  opt={t("book.opt.b")} title={t("book.mode.offline")} sub={t("book.mode.offline.sub")} />
              </div>
            </section>

            <section>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="serif italic text-3xl" style={{ color: "var(--terra)" }}>03</span>
                <h2 className="serif text-2xl md:text-3xl" style={{ letterSpacing: "-0.015em" }}>{t("book.step3")}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border hairline p-4" style={{ background: "#FFFFFF" }} data-testid={BOOKING.calendar}>
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < minDate} className="rounded-none" />
                </div>
                <div>
                  <div className="mono-eyebrow mb-3">{t("book.slots.title")}</div>
                  {!date && <p className="text-xs" style={{ color: "var(--mute)" }}>{t("book.no.date")}</p>}
                  {date && loadingSlots && (
                    <div className="mono-eyebrow flex items-center gap-2" style={{ color: "var(--mute)" }}>
                      <Loader2 size={12} className="animate-spin" /> Loading…
                    </div>
                  )}
                  {date && !loadingSlots && availableSlots.length === 0 && (
                    <p className="text-sm" style={{ color: "var(--mute)" }}>{t("book.no.slots")}</p>
                  )}
                  {date && !loadingSlots && availableSlots.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {availableSlots.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`${BOOKING.slotPrefix}${s.replace(/[^A-Za-z0-9]/g, "")}`}
                          onClick={() => setSlot(s)}
                          className="p-3 border text-sm transition-colors"
                          style={{
                            borderColor: slot === s ? "var(--terra)" : "var(--line)",
                            background: slot === s ? "var(--terra)" : "#FFFFFF",
                            color: slot === s ? "#FFF" : "var(--ink)",
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="serif italic text-3xl" style={{ color: "var(--terra)" }}>04</span>
                <h2 className="serif text-2xl md:text-3xl" style={{ letterSpacing: "-0.015em" }}>{t("book.step4")}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("book.fullname")} testId={BOOKING.nameInput} value={name} onChange={setName} placeholder={t("book.placeholder.name")} />
                <Field label={t("book.phone")} testId={BOOKING.phoneInput} value={phone} onChange={setPhone} placeholder={t("book.placeholder.phone")} />
                <Field label={t("book.email")} testId={BOOKING.emailInput} value={email} onChange={setEmail} type="email" placeholder={t("book.placeholder.email")} className="md:col-span-2" />
                <div className="md:col-span-2">
                  <label className="mono-eyebrow">{t("book.notes")}</label>
                  <textarea
                    data-testid={BOOKING.notesInput}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder={t("book.placeholder.notes")}
                    className="mt-2 w-full p-4 border hairline bg-white focus:outline-none focus:border-black"
                    style={{ borderRadius: 2, fontFamily: "Manrope, sans-serif" }}
                  />
                </div>
              </div>
            </section>

            <section className="border-t hairline pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="mono-eyebrow">{t("book.total")}</div>
                <div className="serif text-4xl md:text-5xl mt-1" style={{ letterSpacing: "-0.02em" }}>
                  ₹500
                  <span className="text-base" style={{ color: "var(--mute)", marginLeft: 10 }}>{t("book.session")}</span>
                </div>
              </div>
              <button
                type="submit"
                data-testid={BOOKING.submit}
                disabled={!canSubmit}
                className="btn-terra"
                style={{ opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? "pointer" : "not-allowed" }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} strokeWidth={1.5} />}
                {submitting ? t("book.preparing") : t("book.continue")}
              </button>
            </section>
          </motion.form>
        </div>
      </main>
    </div>
  );
}

function ModeCard({ active, onClick, testId, opt, title, sub }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="p-5 border text-left transition-colors"
      style={{
        borderColor: active ? "var(--ink)" : "var(--line)",
        background: active ? "var(--ink)" : "#FFFFFF",
        color: active ? "var(--cream)" : "var(--ink)",
      }}
    >
      <div className="mono-eyebrow" style={{ color: active ? "#a3a3a3" : "var(--mute)" }}>{opt}</div>
      <div className="serif text-xl mt-2">{title}</div>
      <div className="text-xs mt-2" style={{ color: active ? "#EAE5D9" : "var(--mute)" }}>{sub}</div>
    </button>
  );
}

function Field({ label, testId, value, onChange, placeholder, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label className="mono-eyebrow">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full p-4 border hairline bg-white focus:outline-none focus:border-black"
        style={{ borderRadius: 2, fontFamily: "Manrope, sans-serif" }}
      />
    </div>
  );
}
